/* eslint-disable react-refresh/only-export-components */
import Highcharts from "highcharts/esm/highcharts.src"

import styled from "styled-components"

import { Button, colors, Text } from "@minuthq/meatball-ui-react"

import { usePostPortfolioAnalyticsViewDetailsClicked } from "src/data/analytics/queries/portfolioAnalyticsAnalyticsQueries"
import { langKeys } from "src/i18n/langKeys"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { useRouter } from "src/router/useRouter"
import { MCard } from "src/ui/MCard/MCard"
import { spacing } from "src/ui/spacing"

import { PortfolioAnalyticsTooltip } from "./PortfolioAnalyticsTooltip"

type PortfolioAnalyticsDeviceStatusTooltipContainerProps = {
  chart: Highcharts.Chart | null
  series?: Highcharts.SeriesOptionsType[]
}

export function PortfolioAnalyticsDeviceStatusTooltipContainer({
  chart,
  series,
}: PortfolioAnalyticsDeviceStatusTooltipContainerProps) {
  return (
    <PortfolioAnalyticsTooltip series={series} chart={chart}>
      {(context) => (
        <PortfolioAnalyticsDeviceStatusTooltipContent context={context} />
      )}
    </PortfolioAnalyticsTooltip>
  )
}

type PortfolioAnalyticsDeviceStatusTooltipContentProps = {
  context: Highcharts.Point[]
}

function PortfolioAnalyticsDeviceStatusTooltipContent({
  context,
}: PortfolioAnalyticsDeviceStatusTooltipContentProps) {
  const { t } = useTranslate()
  const total = context.reduce((sum, point) => sum + (point.y ?? 0), 0) ?? 0
  const { navigate } = useRouter()
  const postPortfolioAnalyticsViewDetailsClicked =
    usePostPortfolioAnalyticsViewDetailsClicked()

  function handleShowDetailsClick() {
    postPortfolioAnalyticsViewDetailsClicked.mutate({
      context: "uptime",
    })

    navigate(
      Routes.Homes.location({
        homegroup_ids: context[0]?.drilldown,
        offline_sensors: "any",
      })
    )
  }

  return (
    <MCard padding={0}>
      <TooltipTitle>
        <Text variant="bodyS">{context[0]?.name}</Text>
      </TooltipTitle>
      <PointsContainer>
        {context.map((point) => {
          if (!point.y) {
            return null
          }

          return (
            <div key={point.series.name}>
              <Text variant="subtitleS">
                {t(langKeys.portfolio_analytics_graph_tooltip, {
                  count: point.y,
                  name: point.series.name,
                })}
              </Text>
              <Text variant="bodyS">
                {total > 0
                  ? `(${(((point.y ?? 0) / total) * 100).toFixed(0)}%)`
                  : "(0%)"}
              </Text>
            </div>
          )
        })}
      </PointsContainer>
      <NavButtonContainer>
        <Button fullWidth variant="subtle" onClick={handleShowDetailsClick}>
          {t(langKeys.view_details)}
        </Button>
      </NavButtonContainer>
    </MCard>
  )
}

const PointsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.M};
  padding: ${spacing.XS} ${spacing.M};
`

const TooltipTitle = styled.div`
  background-color: ${colors.primaryLight};
  padding: ${spacing.XS} ${spacing.M};
`

const NavButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 ${spacing.M} ${spacing.M} ${spacing.M};
`
